import {
  EducationItem,
  FinancialApplication,
  FinancialPerson,
} from '@/api/financial/application/read-application'
import { removeNonNumeric } from '@/helpers'
import { isMarried } from '@/pages/credit/journeys/allowance/allowance-flow'
import { PersonalLoanStep } from './personal-loan-flow'

export type EducationKind = 'g' | 'r' | 'p'

export type PersonalLoanForm = {
  amount: number
  installments: number
  first_due_date: string
  birthdate: string
  gender: string
  zip_code: string
  street: string
  district: string
  number: string
  complement: string
  municipality_id: string
  marital: string
  spouse_name: string
  spouse_tax_id: string
  politically_exposed: string
  us_person: string
  income: string
  purposes: string[]
  purpose_description: string
  has_company: string
  company_tax_id: string
  accounting_proposal: string
  g_institution: string
  g_country: string
  g_status: string
  g_date: string
  residency: boolean
  r_institution: string
  r_country: string
  r_status: string
  r_date: string
  r_specialty: string
  post: boolean
  p_institution: string
  p_country: string
  p_status: string
  p_date: string
  p_specialty: string
  bank_code: string
  agency_number: string
  agency_digit: string
  account_number: string
  account_digit: string
  account_type: string
}

export const DEFAULT_AMOUNT = 20000
export const DEFAULT_INSTALLMENTS = 18

export function emptyForm(): PersonalLoanForm {
  return {
    amount: DEFAULT_AMOUNT,
    installments: DEFAULT_INSTALLMENTS,
    first_due_date: '',
    birthdate: '',
    gender: '',
    zip_code: '',
    street: '',
    district: '',
    number: '',
    complement: '',
    municipality_id: '',
    marital: '',
    spouse_name: '',
    spouse_tax_id: '',
    politically_exposed: '',
    us_person: '',
    income: '',
    purposes: [],
    purpose_description: '',
    has_company: '',
    company_tax_id: '',
    accounting_proposal: '',
    g_institution: '',
    g_country: '',
    g_status: '',
    g_date: '',
    residency: false,
    r_institution: '',
    r_country: '',
    r_status: '',
    r_date: '',
    r_specialty: '',
    post: false,
    p_institution: '',
    p_country: '',
    p_status: '',
    p_date: '',
    p_specialty: '',
    bank_code: '',
    agency_number: '',
    agency_digit: '',
    account_number: '',
    account_digit: '',
    account_type: '',
  }
}

const educationTypes: Record<EducationKind, EducationItem['type']> = {
  g: 'graduation',
  r: 'residency',
  p: 'post_graduation',
}

function yesNo(value: boolean | null | undefined) {
  if (value == null) return ''
  return value ? 'yes' : 'no'
}

function text(value: string | number | null | undefined) {
  if (value == null) return ''
  return String(value)
}

function educationFields(kind: EducationKind, item: EducationItem | undefined) {
  if (!item) return {}
  return {
    [`${kind}_institution`]: item.institution,
    [`${kind}_country`]: item.country ?? '',
    [`${kind}_status`]: item.status,
    [`${kind}_date`]: item.completion_date ?? '',
    ...(kind === 'g' ? {} : { [`${kind}_specialty`]: item.specialty ?? '' }),
  }
}

export function formFromApplication(
  application: FinancialApplication,
): PersonalLoanForm {
  const person: FinancialPerson | undefined =
    application.application_persons[0]?.person
  const address = person?.address[0]
  const bank = person?.bank_account[0]
  const education = person?.education ?? []
  const find = (kind: EducationKind) =>
    education.find((item) => item.type === educationTypes[kind])
  const firstDue = application.simulation?.cashflow[0]?.due_date ?? ''
  return {
    ...emptyForm(),
    amount: application.simulation
      ? Number(application.simulation.amount)
      : DEFAULT_AMOUNT,
    installments: application.simulation?.installments ?? DEFAULT_INSTALLMENTS,
    first_due_date: firstDue.slice(0, 10),
    birthdate: person?.birthdate?.slice(0, 10) ?? '',
    gender: person?.gender ?? '',
    zip_code: address?.zip_code ?? '',
    street: address?.street ?? '',
    district: address?.district ?? '',
    number: address?.number ?? '',
    complement: address?.complement ?? '',
    municipality_id: address ? String(address.municipality_id) : '',
    marital: person?.marital_status ?? '',
    spouse_name: person?.spouse_name ?? '',
    spouse_tax_id: person?.spouse_tax_id ?? '',
    politically_exposed: yesNo(person?.politically_exposed),
    us_person: yesNo(person?.us_person),
    income: text(person?.monthly_income),
    purposes: person?.credit_purposes ?? [],
    purpose_description: person?.credit_purpose_description ?? '',
    has_company: yesNo(person?.has_medical_company),
    company_tax_id: person?.medical_company_tax_id ?? '',
    accounting_proposal: yesNo(person?.accounting_proposal_requested),
    ...educationFields('g', find('g')),
    residency: Boolean(find('r')),
    ...educationFields('r', find('r')),
    post: Boolean(find('p')),
    ...educationFields('p', find('p')),
    bank_code: bank?.bank_code ?? '',
    agency_number: bank?.agency_number ?? '',
    agency_digit: bank?.agency_digit ?? '',
    account_number: bank?.account_number ?? '',
    account_digit: bank?.account_digit ?? '',
    account_type: bank?.account_type ?? '',
  }
}

export const digits = removeNonNumeric

function educationItem(
  form: PersonalLoanForm,
  kind: EducationKind,
): EducationItem {
  const field = (name: string) =>
    String(form[`${kind}_${name}` as keyof PersonalLoanForm] ?? '')
  return {
    type: educationTypes[kind],
    institution: field('institution'),
    country: field('country'),
    status: field('status') === 'in_progress' ? 'in_progress' : 'completed',
    completion_date: field('date') || null,
    specialty: kind === 'g' ? null : field('specialty') || null,
  }
}

/** A formação vai inteira a cada salvamento: graduação, residência e pós. */
export function educationPayload(form: PersonalLoanForm): EducationItem[] {
  const items = [educationItem(form, 'g')]
  if (form.residency) items.push(educationItem(form, 'r'))
  if (form.post) items.push(educationItem(form, 'p'))
  return items
}

export function personPayloadForStep(
  step: PersonalLoanStep,
  form: PersonalLoanForm,
  crm: string | null,
): Record<string, unknown> | null {
  switch (step) {
    case 'eu':
      return {
        birthdate: form.birthdate,
        gender: form.gender,
        ...(crm ? { crm } : {}),
      }
    case 'end':
      return {
        address: {
          zip_code: digits(form.zip_code),
          street: form.street,
          number: form.number,
          complement: form.complement,
          district: form.district,
          municipality_id: Number(form.municipality_id),
        },
      }
    case 'civil': {
      const married = isMarried(form.marital)
      return {
        marital_status: form.marital,
        politically_exposed: form.politically_exposed === 'yes',
        us_person: form.us_person === 'yes',
        spouse_name: married ? form.spouse_name : null,
        spouse_tax_id: married ? digits(form.spouse_tax_id) : null,
      }
    }
    case 'uso':
      return {
        monthly_income: Number(form.income),
        credit_purposes: form.purposes,
        credit_purpose_description: form.purpose_description || null,
        has_medical_company:
          form.has_company === '' ? null : form.has_company === 'yes',
        medical_company_tax_id:
          form.has_company === 'yes' ? digits(form.company_tax_id) : null,
        accounting_proposal_requested:
          form.accounting_proposal === ''
            ? null
            : form.accounting_proposal === 'yes',
      }
    case 'grad':
    case 'espec':
      return { education: educationPayload(form) }
    case 'conta':
      return {
        bank_account: {
          bank_code: form.bank_code,
          agency_number: form.agency_number,
          agency_digit: form.agency_digit,
          account_number: form.account_number,
          account_digit: form.account_digit,
          account_type: form.account_type,
        },
      }
    default:
      return null
  }
}

function educationValid(form: PersonalLoanForm, kind: EducationKind) {
  const value = (name: string) =>
    form[`${kind}_${name}` as keyof PersonalLoanForm]
  return Boolean(
    value('institution') &&
      value('country') &&
      value('status') &&
      /^\d{4}-\d{2}$/.test(String(value('date'))) &&
      (kind === 'g' || value('specialty')),
  )
}

export function isStepValid(
  step: PersonalLoanStep,
  form: PersonalLoanForm,
  context: {
    hasPreview: boolean
    hasRequiredDocs: boolean
    hasProposal: boolean
    denied: boolean
    hasVerifiedPhone: boolean
  },
): boolean {
  switch (step) {
    case 'valor':
      return context.hasPreview && Boolean(form.first_due_date)
    case 'eu':
      return Boolean(context.hasVerifiedPhone && form.birthdate && form.gender)
    case 'end':
      return Boolean(
        form.zip_code &&
          form.street &&
          form.number &&
          form.district &&
          form.municipality_id,
      )
    case 'civil':
      return Boolean(
        form.marital &&
          form.politically_exposed &&
          form.us_person &&
          (!isMarried(form.marital) ||
            (form.spouse_name && digits(form.spouse_tax_id).length === 11)),
      )
    case 'uso':
      return (
        Number(form.income) > 0 &&
        form.purposes.length > 0 &&
        (form.has_company === 'no' ||
          (form.has_company === 'yes' &&
            digits(form.company_tax_id).length === 14))
      )
    case 'grad':
      return educationValid(form, 'g')
    case 'espec':
      return (
        (!form.residency || educationValid(form, 'r')) &&
        (!form.post || educationValid(form, 'p'))
      )
    case 'conta':
      return Boolean(
        form.bank_code &&
          form.agency_number &&
          form.account_number &&
          form.account_digit &&
          form.account_type,
      )
    case 'docs':
      return context.hasRequiredDocs
    case 'analise':
      return !context.denied
    case 'proposta':
      return context.hasProposal
    case 'assinar':
      return false
    default:
      return true
  }
}

/* ---------- rascunho local: o que ainda não foi salvo no servidor ---------- */

export type PersonalLoanDraft = Partial<PersonalLoanForm> & {
  address_state?: string
}

const storageKey = (applicationKey: string) => `credit-draft:${applicationKey}`

export function readDraft(applicationKey: string): PersonalLoanDraft {
  try {
    const raw = localStorage.getItem(storageKey(applicationKey))
    const parsed = raw ? (JSON.parse(raw) as unknown) : null
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
      return {}
    const record = parsed as Record<string, unknown>
    const sample = emptyForm()
    const draft: PersonalLoanDraft = {}
    for (const key of Object.keys(sample) as (keyof PersonalLoanForm)[]) {
      if (key in record && typeof record[key] === typeof sample[key]) {
        Object.assign(draft, { [key]: record[key] })
      }
    }
    if (typeof record.address_state === 'string')
      draft.address_state = record.address_state
    return draft
  } catch {
    return {}
  }
}

export function writeDraft(applicationKey: string, partial: PersonalLoanDraft) {
  try {
    const next = { ...readDraft(applicationKey), ...partial }
    localStorage.setItem(storageKey(applicationKey), JSON.stringify(next))
  } catch {
    // sem armazenamento local, o rascunho só vive na tela
  }
}

const educationKeys = (kind: EducationKind) =>
  ['institution', 'country', 'status', 'date', 'specialty'].map(
    (name) => `${kind}_${name}` as keyof PersonalLoanForm,
  )

/** Campos que cada etapa grava no servidor e que saem do rascunho depois. */
export const stepFields: Partial<
  Record<PersonalLoanStep, (keyof PersonalLoanDraft)[]>
> = {
  valor: ['amount', 'installments', 'first_due_date'],
  eu: ['birthdate', 'gender'],
  end: [
    'zip_code',
    'street',
    'district',
    'number',
    'complement',
    'municipality_id',
    'address_state',
  ],
  civil: [
    'marital',
    'spouse_name',
    'spouse_tax_id',
    'politically_exposed',
    'us_person',
  ],
  uso: [
    'income',
    'purposes',
    'purpose_description',
    'has_company',
    'company_tax_id',
    'accounting_proposal',
  ],
  grad: educationKeys('g'),
  espec: ['residency', 'post', ...educationKeys('r'), ...educationKeys('p')],
  conta: [
    'bank_code',
    'agency_number',
    'agency_digit',
    'account_number',
    'account_digit',
    'account_type',
  ],
}

export function clearDraftFields(
  applicationKey: string,
  fields: (keyof PersonalLoanDraft)[],
) {
  const current = readDraft(applicationKey)
  for (const field of fields) delete current[field]
  try {
    if (Object.keys(current).length === 0)
      localStorage.removeItem(storageKey(applicationKey))
    else
      localStorage.setItem(storageKey(applicationKey), JSON.stringify(current))
  } catch {
    // sem armazenamento local
  }
}

export function clearDraft(applicationKey: string) {
  try {
    localStorage.removeItem(storageKey(applicationKey))
  } catch {
    // nada a limpar
  }
}

export function mergeDraft(
  base: PersonalLoanForm,
  draft: PersonalLoanDraft,
): PersonalLoanForm {
  const { address_state: _addressState, ...fields } = draft
  return { ...base, ...fields }
}
